import firebase from 'firebase/app';
import { Collection, getFirestore } from 'firestorter';

import CollectionName from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';
import { UserCategory } from '../../Model/User';

const SCHEDULED_STATUS = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  CANCELED: 'CANCELED',
};

const ANNOUNCEMENT_USER_TYPE_TAG_NAMES = {
  [UserCategory.OTHER]: { label: '1:1 Coaching', value: '1_1_COACHING' },
  [UserCategory.CHALLENGES]: { label: 'Challenges', value: 'CHALLENGES' },
  [UserCategory.AI_FRIEND]: { label: 'AI Friend', value: 'AI_FRIEND' },
};

const POD_NOT_REQUIRED_TAGS = [
  ANNOUNCEMENT_USER_TYPE_TAG_NAMES.CHALLENGES.value,
  ANNOUNCEMENT_USER_TYPE_TAG_NAMES.AI_FRIEND.value,
];

// A post still counts as active when it is live in the feed or scheduled on its way there.
// Scheduled posts are stored isRemoved: true until the server publishes them, so `isRemoved` alone
// cannot tell a pending post from a deleted one - scheduledStatus is the discriminator.
const isActiveAnnouncement = ({ isRemoved, scheduledStatus }) => (
  !isRemoved || scheduledStatus === SCHEDULED_STATUS.PENDING
);

// Whether a post is worth a group read. Every post written since multi-select carries a
// `basePostId`, single-community ones included, so the id alone does not mean there are siblings.
// `groupCommunityIds` can over-report a group that shrank but never under-report one, so a single
// entry is a safe signal to skip the query.
const hasCommunityGroup = ({ basePostId, groupCommunityIds = [] } = {}) => (
  !!basePostId && groupCommunityIds.length > 1
);

// S2-8388: mirrors the server's PostModerationCategory (functions-22/src/announcement/constants.ts).
const POST_MODERATION_CATEGORY_LABELS = {
  ABUSIVE_CONTENT: 'Abusive content',
  CS_ISSUE: 'CS issue',
  DIRECT_LINE_ROUTING: 'Routing around community',
  EXPECTATION_MISMATCH: 'Expectation mismatch',
  MANUAL: 'Hidden by coach',
};

/**
 * Class representing an Announcement.
 * An announcement is a post that is shared to a group of clients.
 *
 * @class Announcement
 * @extends BaseDocument
 */
class Announcement extends BaseDocument {
  constructor(id, opts) {
    super(`${CollectionName.ANNOUNCEMENT}/${id}`, opts);
  }

  /**
   * The owner of the announcement.
   * @returns {string}
   */
  get coach() {
    return this.data.coach;
  }

  /**
   * The message of the announcement.
   * @returns {string}
   */
  get message() {
    return this.data.message;
  }

  /**
   * The id of the user tag associated with the announcement.
   * If empty, the announcement belongs to all clients.
   * @returns {string}
   */
  get tagId() {
    return this.data.tagId || '';
  }

  /**
   * The name of the user tag associated with the announcement.
   * @returns {string}
   */
  get tagName() {
    return this.data.tagName || '';
  }

  /**
   * The attachments data of the announcement.
   * @returns {Object[]}
   */
  get attachments() {
    return this.data.attachments || [];
  }

  /**
   * The number of likes on the announcement.
   * @returns {number}
   */
  get likes() {
    return this.data.likes || 0;
  }

  /**
   * The date and time the announcement was created.
   * @returns {Date}
   */
  get createdAt() {
    return this.data.createdAt;
  }

  /**
   * The user who created the announcement.
   * @returns {string}
   */
  get createdBy() {
    return this.data.createdBy;
  }

  /**
   * The date and time the announcement was last updated.
   * @returns {Date}
   */
  get lastUpdatedAt() {
    return this.data.lastUpdatedAt;
  }

  /**
   * The user who last updated the announcement.
   * @returns {string}
   */
  get lastUpdatedBy() {
    return this.data.lastUpdatedBy;
  }

  /**
   * Whether the announcement is removed.
   * @returns {boolean}
   */
  get isRemoved() {
    return !!this.data.isRemoved;
  }

  /**
   * The pod comment counts map for the announcement.
   * @returns {Object}
   */
  get podComments() {
    return this.data.podComments || {};
  }

  /**
   * The number of comments on the announcement.
   * @returns {number}
   */
  get comments() {
    return this.data.comments || 0;
  }

  /**
   * The avatar url of the post's author.
   * @returns {string}
   */
  get postAvatarUrl() {
    return this.data.postAvatarUrl || '';
  }

  /**
   * The name of the post author.
   * @returns {string}
   */
  get postUserName() {
    return this.data.postUserName || '';
  }

  /**
   * Whether the post is created by the coach.
   * @returns {boolean}
   */
  get isByCoach() {
    return this.data.isByCoach || false;
  }

  /**
   * The id of the pod associated with the announcement.
   * @returns {string | null}
   */
  get podId() {
    return this.data.podId || null;
  }

  /**
   * The community this post belongs to: a sentinel category id
   * ('1_1_COACHING' | 'CHALLENGES' | 'AI_FRIEND') or a POD/CUSTOM community doc id.
   * Absent on legacy posts created before the community migration.
   * @returns {string}
   */
  get communityId() {
    return this.data.communityId || '';
  }

  get communityName() {
    return this.data.communityName || '';
  }

  /**
   * Group key for a multi-community post: the sibling docs (one per community) all share one
   * value, equal to the first sibling's own doc id. Absent on posts created before multi-select,
   * so callers treat those as a group of one via `basePostId || id`.
   * @returns {string}
   */
  get basePostId() {
    return this.data.basePostId || '';
  }

  /**
   * Display only. Every community id the group targeted when it was created. Denormalized, so it
   * can go stale once a sibling is removed - never resolve an audience or membership from it.
   * @returns {string[]}
   */
  get groupCommunityIds() {
    return this.data.groupCommunityIds || [];
  }

  get isPinned() {
    return !!this.data.isPinned;
  }

  /**
   * Whether the post is hidden from other community members (S2-8388) — set by the AI
   * moderation trigger or a coach's manual hide action. Author and coach can still see it.
   * @returns {boolean}
   */
  get isHidden() {
    return !!this.data.isHidden;
  }

  /**
   * The category the post was hidden for (see server's PostModerationCategory) — one of the
   * AI categories, or MANUAL for a coach-initiated hide.
   * @returns {string}
   */
  get hiddenCategory() {
    return this.data.hiddenCategory || '';
  }

  /**
   * Current announcement scheduled status from @type {SCHEDULED_STATUS}
   * Only set for scheduled posts (PENDING, COMPLETED, CANCELED). Post-now posts have no scheduledStatus.
   * @returns {string}
   */
  get scheduledStatus() {
    return this.data.scheduledStatus || '';
  }

  /**
   * Intended publish time: scheduled time or current time for post-now.
   * @returns {Date | null}
   */
  get publishAt() {
    return this.data.publishAt || null;
  }

  /**
   * When the announcement was canceled (scheduled or removed from feed).
   * @returns {Date | undefined}
   */
  get canceledAt() {
    return this.data.canceledAt;
  }

  /**
   * User id who canceled the announcement.
   * @returns {string | undefined}
   */
  get canceledBy() {
    return this.data.canceledBy;
  }

  /**
   * Cancels a scheduled announcement or removes a published one from the feed.
   * canceledAt / canceledBy are only stored when canceling a pending scheduled post (audit for schedule cancel).
   * @param {string} canceledBy - Firebase user id of who performed the action.
   * @returns {Promise<void>}
   */
  async remove(canceledBy) {
    return this.updateFields(Announcement.getRemovalFields(this, canceledBy));
  }

  /**
   * The fields that remove a post, shared by the single-doc and batched group paths so the
   * PENDING -> CANCELED audit branch cannot drift between them.
   * @param {Object} announcement - Anything exposing `scheduledStatus`.
   * @param {string} canceledBy - Firebase user id of who performed the action.
   * @returns {Object}
   */
  static getRemovalFields({ scheduledStatus }, canceledBy) {
    if (scheduledStatus === SCHEDULED_STATUS.PENDING) {
      return {
        canceledAt: new Date(),
        canceledBy,
        isRemoved: true,
        scheduledStatus: SCHEDULED_STATUS.CANCELED,
      };
    }
    return {
      isRemoved: true,
    };
  }

  async pin(userId) {
    return this.updateFields({
      isPinned: true,
      pinnedAt: new Date(),
      pinnedBy: userId,
    });
  }

  async unpin(userId) {
    return this.updateFields({
      isPinned: false,
      unpinnedAt: new Date(),
      unpinnedBy: userId,
    });
  }

  /**
   * Hides the post from other community members (S2-8388). One-directional — no unhide;
   * reverting a mistaken hide is a direct Firestore write, not a built feature.
   * @param {string} hiddenBy - Firebase user id of the coach performing the hide.
   * @param {string} [category] - PostModerationCategory value; defaults to a manual hide.
   * @returns {Promise<void>}
   */
  async hide(hiddenBy, category = 'MANUAL') {
    return this.updateFields({
      isHidden: true,
      hiddenBy,
      hiddenCategory: category,
    });
  }

  static async replacePinnedPost(oldDoc, newDoc, userId) {
    const batch = firebase.firestore().batch();
    batch.update(oldDoc.ref, {
      isPinned: false,
      unpinnedAt: new Date(),
      unpinnedBy: userId,
    });
    batch.update(newDoc.ref, { isPinned: true, pinnedAt: new Date(), pinnedBy: userId });
    return batch.commit();
  }

  /**
   * Updates the announcement in firestore.
   * @param {Object} data - JSON data to be attached to the new announcement doc
   * @returns {Promise<void>}
   */
  async updateDoc(data) {
    return this.updateFields(data);
  }

  /**
   * Creates one announcement doc per targeted community, linked as a group.
   *
   * The refs are generated up front so `basePostId` (the first sibling's own id) is known before
   * the write, letting all the siblings go in a single batch - a group can never half-exist.
   *
   * @param {Object[]} dataList - One payload per community, each carrying its own communityId.
   * @returns {Promise<{basePostId: string, ids: string[]}>}
   */
  static async addGroupDocs(dataList) {
    const collectionRef = getFirestore().collection(CollectionName.ANNOUNCEMENT);
    const refs = dataList.map(() => collectionRef.doc());
    const basePostId = refs[0].id;
    const groupCommunityIds = dataList.map(({ communityId }) => communityId);

    const batch = getFirestore().batch();
    refs.forEach((ref, index) => {
      batch.set(ref, {
        ...dataList[index],
        basePostId,
        groupCommunityIds,
      });
    });
    await batch.commit();

    return { basePostId, ids: refs.map(({ id }) => id) };
  }

  /**
   * Every doc in a post's group, including the one asked about.
   *
   * Deliberately a single equality filter: that is served by the automatic single-field index, so
   * adding an orderBy or a second where here would need a composite index deploy. Sorting and
   * active/removed filtering are therefore the caller's job, in memory.
   *
   * @param {string} basePostId - Group key.
   * @returns {Promise<Announcement[]>} Siblings, oldest first.
   */
  static async getGroupSiblings(basePostId) {
    const siblingsCollection = new Collection(CollectionName.ANNOUNCEMENT, {
      createDocument: ({ id }, opts) => new Announcement(id, { ...opts, disableObserverRefCount: true }),
      query: (ref) => ref.where('basePostId', '==', basePostId),
    });
    await siblingsCollection.fetch();
    return siblingsCollection.docs
      .slice()
      .sort((a, b) => (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0));
  }

  /**
   * The copies of this post that are still in play - live in a feed, or scheduled on their way
   * there. Falls back to the post itself when it has no siblings, or when nothing active came
   * back, so callers always get something to work with.
   *
   * @param {Announcement} announcement - Any sibling of the group.
   * @returns {Promise<Announcement[]>} Active siblings, oldest first.
   */
  static async getActiveGroupSiblings(announcement) {
    if (!hasCommunityGroup(announcement)) {
      return [announcement];
    }
    const siblings = await Announcement.getGroupSiblings(announcement.basePostId);
    const activeSiblings = siblings.filter(isActiveAnnouncement);
    return activeSiblings.length ? activeSiblings : [announcement];
  }

  /**
   * Applies a whole group's worth of changes in one batch, so a group cannot end up half updated.
   *
   * @param {Object} params
   * @param {Object[]} [params.updates] - `{ announcement, fields }` pairs to update.
   * @param {Object[]} [params.creates] - Payloads for new siblings joining the group.
   * @param {Announcement[]} [params.removals] - Siblings to soft-remove.
   * @param {string} [params.canceledBy] - Firebase user id, for the removal audit fields.
   * @returns {Promise<void>}
   */
  static async commitGroupChanges({
    updates = [],
    creates = [],
    removals = [],
    canceledBy,
  }) {
    const firestore = getFirestore();
    const batch = firestore.batch();
    const collectionRef = firestore.collection(CollectionName.ANNOUNCEMENT);

    updates.forEach(({ announcement, fields }) => batch.update(announcement.ref, fields));
    creates.forEach((data) => batch.set(collectionRef.doc(), data));
    removals.forEach((announcement) => batch.update(
      announcement.ref,
      Announcement.getRemovalFields(announcement, canceledBy),
    ));

    return batch.commit();
  }

  /**
   * Removes a post and, when it belongs to a group, every sibling with it - deleting a
   * multi-community post takes it out of all of its communities.
   *
   * @param {Announcement} announcement - Any sibling of the group.
   * @param {string} canceledBy - Firebase user id of who performed the action.
   * @returns {Promise<void>}
   */
  static async removeGroup(announcement, canceledBy) {
    if (!hasCommunityGroup(announcement)) {
      return announcement.remove(canceledBy);
    }
    return Announcement.commitGroupChanges({
      removals: await Announcement.getActiveGroupSiblings(announcement),
      canceledBy,
    });
  }

  /**
   * Gets the announcements for a coach, optionally bounded by a createdAt date range.
   *
   * @param {string} coachId - Id of the coach
   * @param {Object} [options]
   * @param {Date|null} [options.sinceDate] - inclusive lower bound on createdAt
   * @param {Date|null} [options.untilDate] - exclusive upper bound on createdAt
   * @returns {Promise<Collection>} The collection of announcements from firestore
   */
  static async getAnnouncementsByCoach(coachId, { sinceDate = null, untilDate = null } = {}) {
    const announcementsCollection = new Collection(CollectionName.ANNOUNCEMENT, {
      createDocument: ({ id }, opts) => new Announcement(id, { ...opts, disableObserverRefCount: true }),
      query: (ref) => {
        let query = ref
          .where('coach', '==', coachId)
          .where('isRemoved', '==', false);
        if (sinceDate) {
          query = query.where('createdAt', '>=', sinceDate);
        }
        if (untilDate) {
          query = query.where('createdAt', '<', untilDate);
        }
        return query.orderBy('createdAt', 'desc');
      },
    });
    await announcementsCollection.fetch();
    return announcementsCollection;
  }

  /**
   * Gets all pinned (non-removed) announcements for a coach, regardless of date.
   * Small result set (pinning is capped per category) and intentionally unbounded
   * so pinned posts always show on top even when older than the selected date range.
   *
   * @param {string} coachId - Id of the coach
   * @returns {Promise<Collection>} The collection of pinned announcements
   */
  static async getPinnedAnnouncementsByCoach(coachId) {
    const pinnedCollection = new Collection(CollectionName.ANNOUNCEMENT, {
      createDocument: ({ id }, opts) => new Announcement(id, { ...opts, disableObserverRefCount: true }),
      query: (ref) => ref
        .where('coach', '==', coachId)
        .where('isRemoved', '==', false)
        .where('isPinned', '==', true)
        .orderBy('createdAt', 'desc'),
    });
    await pinnedCollection.fetch();
    return pinnedCollection;
  }

  /**
   * Fetches pending announcements for a coach, sorted by earliest publish time.
   * @param {string} coachId - Id of the coach
   * @returns {Promise<Collection>} Pending/scheduled announcements
   */
  static async getScheduledAnnouncementsByCoach(coachId) {
    const scheduledCollection = new Collection(CollectionName.ANNOUNCEMENT, {
      createDocument: ({ id }, opts) => new Announcement(id, { ...opts, disableObserverRefCount: true }),
      query: (ref) => ref
        .where('coach', '==', coachId)
        .where('scheduledStatus', '==', SCHEDULED_STATUS.PENDING)
        .orderBy('publishAt', 'asc'),
    });
    await scheduledCollection.fetch();
    return scheduledCollection;
  }

  /**
   * Gets an announcement by its id.
   *
   * @param {string} id - Id of the announcement
   * @returns {Promise<Announcement>} The announcement from firestore
   */
  static async getAnnouncementById(id) {
    const announcementDoc = new Announcement(id);
    await announcementDoc.init();
    return announcementDoc;
  }
}

export default Announcement;
export {
  ANNOUNCEMENT_USER_TYPE_TAG_NAMES,
  POD_NOT_REQUIRED_TAGS,
  SCHEDULED_STATUS,
  POST_MODERATION_CATEGORY_LABELS,
  hasCommunityGroup,
};
